<template>
  <div class="split-screen-view">
    <NavigationBar
      titel="Personal - Split Screen"
      :actions="actions"
      showBackBtn
      isPreview
      dense
    />

    <div class="split-screen-container">
      <!-- Mobile/Small Screen Alert -->
      <v-alert
        v-if="$vuetify.breakpoint.smAndDown"
        type="warning"
        border="left"
        colored-border
        icon="mdi-monitor-cellphone"
        prominent
        elevation="2"
        class="ma-4"
      >
        <div class="title">Split-Screen nicht verfügbar</div>
        <div class="mt-2">
          Split Screen ist auf kleinen Bildschirmen nicht verfügbar.
        </div>
      </v-alert>

      <!-- Split Pane Container - Hidden on small screens -->
      <div
        v-else-if="$vuetify.breakpoint.mdAndUp"
        class="split-pane-wrapper split-horizontal"
      >
        <!-- Left/Top Pane -->
        <div
          ref="primaryPane"
          class="split-pane split-pane-primary"
          :style="primaryPaneStyle"
        >
          <personnel-record-pane
            :organizationId="$route.params.organizationId"
            :selectedRecordId="selectedRecordIds[0]"
            @update="updateSelectedRecord(0, $event)"
            :records="records"
            panePosition="primary"
            :containerWidth="primaryPaneWidth"
          />
        </div>

        <!-- Resizable Divider -->
        <div
          class="split-divider split-divider-horizontal"
          @mousedown="startResize"
        >
          <div class="split-divider-handle">
            <v-icon small color="grey">mdi-drag-vertical</v-icon>
          </div>
        </div>

        <!-- Right/Bottom Pane -->
        <div
          ref="secondaryPane"
          class="split-pane split-pane-secondary"
          :style="secondaryPaneStyle"
        >
          <personnel-record-pane
            :organizationId="$route.params.organizationId"
            :selectedRecordId="selectedRecordIds[1]"
            @update="updateSelectedRecord(1, $event)"
            :records="records"
            panePosition="secondary"
            :containerWidth="secondaryPaneWidth"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";
import PersonnelRecordPane from "@/components/member-management/personnel-records/PersonnelRecordPane.vue";
import { MEMBER_MANAGEMENT } from "@/store/modules.js";
import { GET_PERSONNEL_RECORDS } from "@/store/action-types.js";

export default {
  name: "member-management-personnel-records-split-screen",
  components: {
    NavigationBar,
    PersonnelRecordPane,
  },
  data() {
    return {
      splitPosition: 50, // percentage
      isResizing: false,
      selectedRecordIds: [null, null],
      primaryPaneWidth: 0,
      secondaryPaneWidth: 0,
      paneResizeObserver: null,
      minPaneWidthPx: 300, // Minimum width for a pane in pixels
      actions: [{ actionStyle: "clipboardBtn" }],
    };
  },
  computed: {
    records() {
      return this.$store.state.memberManagement.personnelRecords;
    },
    primaryPaneStyle() {
      return { width: `${this.splitPosition}%` };
    },
    secondaryPaneStyle() {
      return { width: `${100 - this.splitPosition}%` };
    },
    // Calculate current minimum constraints for user feedback
    currentMinConstraints() {
      const container = document.querySelector(".split-pane-wrapper");
      if (!container) return { minPercentage: 20, maxPercentage: 80 };

      const rect = container.getBoundingClientRect();
      const minPercentage = (this.minPaneWidthPx / rect.width) * 100;
      return {
        minPercentage: Math.max(minPercentage, 10), // Never less than 10%
        maxPercentage: Math.min(100 - minPercentage, 90), // Never more than 90%
      };
    },
  },
  watch: {
    // Watch for changes in selected record IDs and update URL
    selectedRecordIds: {
      deep: true,
      handler() {
        this.updateRouteQuery();
      },
    },
    // Watch for route changes to update selections
    "$route.query": {
      immediate: true,
      handler(newQuery) {
        this.initializeFromQuery(newQuery);
      },
    },
  },
  created() {
    this.initialize();
  },
  mounted() {
    this.setupPaneResizeObserver();
  },
  beforeDestroy() {
    this.stopResize();
    this.teardownPaneResizeObserver();
  },
  methods: {
    initialize() {
      this.$store.dispatch(`${MEMBER_MANAGEMENT}${GET_PERSONNEL_RECORDS}`, {
        organizationId: this.$route.params.organizationId,
      });
    },
    initializeFromQuery(query) {
      // Initialize selected records from query parameters
      const primaryId = query.primary || null;
      const secondaryId = query.secondary || null;

      // Only update if the values have actually changed to avoid infinite loops
      if (
        this.selectedRecordIds[0] !== primaryId ||
        this.selectedRecordIds[1] !== secondaryId
      ) {
        this.$set(this.selectedRecordIds, 0, primaryId);
        this.$set(this.selectedRecordIds, 1, secondaryId);
      }
    },
    updateRouteQuery() {
      // Build query object
      const query = {};
      if (this.selectedRecordIds[0]) query.primary = this.selectedRecordIds[0];
      if (this.selectedRecordIds[1])
        query.secondary = this.selectedRecordIds[1];

      // Only update route if query has actually changed
      const currentQuery = this.$route.query;
      const queryChanged =
        currentQuery.primary !== query.primary ||
        currentQuery.secondary !== query.secondary;

      if (queryChanged) {
        this.$router
          .push({
            path: this.$route.path,
            query: query,
          })
          .catch(() => {}); // Ignore NavigationDuplicated errors
      }
    },
    updateSelectedRecord(index, recordId) {
      this.$set(this.selectedRecordIds, index, recordId);
    },
    startResize(e) {
      this.isResizing = true;
      e.preventDefault();
      document.addEventListener("mousemove", this.handleResize);
      document.addEventListener("mouseup", this.stopResize);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    handleResize(e) {
      if (!this.isResizing) return;

      const container = document.querySelector(".split-pane-wrapper");
      if (!container) return;

      const rect = container.getBoundingClientRect();
      let newPosition = ((e.clientX - rect.left) / rect.width) * 100;

      // Calculate minimum percentages based on container width and minimum pane width
      const minPercentage = (this.minPaneWidthPx / rect.width) * 100;
      const maxPercentage = 100 - minPercentage;

      // Constrain between minimum percentages
      newPosition = Math.max(
        minPercentage,
        Math.min(maxPercentage, newPosition)
      );

      this.splitPosition = newPosition;
    },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener("mousemove", this.handleResize);
      document.removeEventListener("mouseup", this.stopResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },
    setupPaneResizeObserver() {
      if (!("ResizeObserver" in window)) {
        console.warn(
          "ResizeObserver not available - pane width tracking disabled"
        );
        return;
      }

      // Track cached breakpoint names to only update when breakpoints change
      let cachedPrimaryBreakpoint = null;
      let cachedSecondaryBreakpoint = null;

      const getBreakpointForWidth = (width) => {
        const breakpoints = { xs: 600, sm: 960, md: 1264, lg: 1904 };
        if (width >= breakpoints.lg) return "xl";
        if (width >= breakpoints.md) return "lg";
        if (width >= breakpoints.sm) return "md";
        if (width >= breakpoints.xs) return "sm";
        return "xs";
      };

      this.paneResizeObserver = new ResizeObserver((entries) => {
        // Process entries immediately but only update if breakpoint changes
        for (const entry of entries) {
          const element = entry.target;
          const width = entry.borderBoxSize
            ? entry.borderBoxSize[0].inlineSize
            : entry.contentRect.width;

          if (element === this.$refs.primaryPane) {
            const newBreakpoint = getBreakpointForWidth(width);
            // Only update if breakpoint actually changes (prevents re-renders during resize)
            if (newBreakpoint !== cachedPrimaryBreakpoint) {
              cachedPrimaryBreakpoint = newBreakpoint;
              this.primaryPaneWidth = width;
            }
          } else if (element === this.$refs.secondaryPane) {
            const newBreakpoint = getBreakpointForWidth(width);
            // Only update if breakpoint actually changes (prevents re-renders during resize)
            if (newBreakpoint !== cachedSecondaryBreakpoint) {
              cachedSecondaryBreakpoint = newBreakpoint;
              this.secondaryPaneWidth = width;
            }
          }
        }
      });

      // Start observing once components are mounted
      this.$nextTick(() => {
        if (this.$refs.primaryPane) {
          this.paneResizeObserver.observe(this.$refs.primaryPane);
          this.primaryPaneWidth = this.$refs.primaryPane.offsetWidth;
          cachedPrimaryBreakpoint = getBreakpointForWidth(
            this.primaryPaneWidth
          );
        }
        if (this.$refs.secondaryPane) {
          this.paneResizeObserver.observe(this.$refs.secondaryPane);
          this.secondaryPaneWidth = this.$refs.secondaryPane.offsetWidth;
          cachedSecondaryBreakpoint = getBreakpointForWidth(
            this.secondaryPaneWidth
          );
        }
      });
    },
    teardownPaneResizeObserver() {
      if (this.paneResizeObserver) {
        this.paneResizeObserver.disconnect();
        this.paneResizeObserver = null;
      }
    },
    updatePaneDimensions() {
      if (this.$refs.primaryPane) {
        this.primaryPaneWidth = this.$refs.primaryPane.offsetWidth;
      }
      if (this.$refs.secondaryPane) {
        this.secondaryPaneWidth = this.$refs.secondaryPane.offsetWidth;
      }
    },
  },
};
</script>

<style scoped>
.split-screen-view {
  /* height: 100vh; */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.split-screen-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.split-controls {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .split-controls {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.split-pane-wrapper {
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
}

.split-pane-wrapper.split-horizontal {
  flex-direction: row;
}

.split-pane {
  overflow: hidden;
  position: relative;
  min-width: 400px; /* Minimum width for horizontal splits */
}

.split-divider {
  position: relative;
  background-color: #e0e0e0;
  /* z-index: 7; */
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme--dark .split-divider {
  background-color: #424242;
}

.split-divider-horizontal {
  width: 8px;
  cursor: col-resize;
  flex-shrink: 0;
}

.split-divider:hover {
  background-color: #1976d2; /* Primary blue for light theme */
}

.theme--dark .split-divider:hover {
  background-color: #2196f3; /* Lighter blue for dark theme */
}

.split-divider-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 2px;
}

.theme--dark .split-divider-handle {
  background-color: rgba(66, 66, 66, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.split-divider-horizontal .split-divider-handle {
  height: 50px;
}
</style>
